import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { comnEnvs, comnUtils } from "@/comn/utils";
import { Button, Grid, Group, Layout, Page } from "@/comn/components";
import { useFetch, useForm, useGrid, useStore, useToast, useResource, useModal, usePage } from "@/comn/hooks";
import {
    APIS,
    SF_ORG_USR_SRCH,
    SG_ORG_USR_LIST,
    URLS,
} from "@/tra/tancis/ptli/usr/orgusraprv/services/PtliOrgUsrAprvService";

/*
 * Organization User Management List
 * !== 기관 사용자 관리 목록 ==!
 */
export const PTLI0921001Q = (): JSX.Element => {
    const { pgeUid, menuId } = usePage({
        auth: ["auth_ptli0015", "auth_ptlianyADM"],
    }); 
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal();
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const { pgeStore, setStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!

    useResource({
        defaultSchema: [{ area: "orgCd" }, { area: "comnCd", comnCd: "PTL0001" }],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // Organization Management List Search !== 기관 관리 목록 검색 ==!
        orgList: useForm({
            defaultSchema: SF_ORG_USR_SRCH,
            defaultValues: { ...pgeStore?.form },
        }),
    };

    /*
     * Declaration Grid
     * !== 그리드 정의 ==!
     */
    const grid = {
        // Organization Management List !== 기관 관리 목록 ==!
        orgUsrList: useGrid({
            defaultSchema: SG_ORG_USR_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        //Check Organization List !==  기관 관리 목록 조회==!
        getOrgUsrAprvList: useFetch({
            api: (page = grid.orgUsrList.page) =>
                APIS.getOrgUsrAprvList(form.orgList.getValues(), page, grid.orgUsrList.size),
            enabled: comnUtils.isEmpty(form.orgList.errors),
            key: [grid.orgUsrList.page, grid.orgUsrList.size],
            onSuccess: () => {
                setStore(pgeUid, {
                    form: form.orgList.getValues(),
                    page: grid.orgUsrList.page,
                    size: grid.orgUsrList.size,
                });
            },
            onError: () => toast.showToast({ type: "error", content: "msg.com.00025" }), // Failed to select. !== 조회가 실패하였습니다. ==!
            showToast: true,
        }),
    };
    const render = {
        cell: {
            psnTin: (props: any) => {
                const { rowValues, value } = props;
                return <Link to={`/ptli/usr/orgusraprv/ptli0921002s/${rowValues.xtrnUsrUuid}`}>{value}</Link>;
            },
        },
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        // Click Search Button !== 검색버튼 ==!
        onSrchBtnClick: (page: number = 0) => {
            grid.orgUsrList.setPage(page);
            fetch.getOrgUsrAprvList.fetch(page);
        },
    };

    /*
     * Declaration Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {
        handler.onSrchBtnClick(pgeStore?.page);
    }, []);

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.orgList.schema.orgCd}></Group.Control>
                            <Group.Control {...form.orgList.schema.psnTin}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.orgList.schema.usrNm}></Group.Control>
                        </Group.Row>
                    </Group.Section>
                    <Layout direction="row">
                        <Layout.Left>
                            <Button
                                role={"reset"}
                                onClick={() => {
                                    form.orgList.reset();
                                }}
                            >
                                {t("B_RESET")}
                            </Button>
                        </Layout.Left>
                        <Layout.Right>
                            <Button
                                role={"search"}
                                onClick={() => {
                                    handler.onSrchBtnClick();
                                }}
                            >
                                {t("B_SRCH")}
                            </Button>
                        </Layout.Right>
                    </Layout>
                </Group.Body>
            </Group>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Grid
                            {...grid.orgUsrList.grid}
                            data={fetch.getOrgUsrAprvList.data?.orgUsrList}
                            render={render}
                        />
                    </Group.Section>
                </Group.Body>
            </Group>
        </Page>
    );
};
